import type {
  DownloadAvailability,
  DownloadItem,
  LoginResponse,
  MeResponse,
} from "../types";
import { getToken } from "./session";

// All calls go through "/api/*", which the Vite dev proxy (and nginx in prod)
// rewrites onto the Vaas.Api base, stripping the "/api" prefix.
const API_BASE = "/api";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch {
      /* non-JSON error body — keep default message */
    }
    if (res.status === 401 && path.includes("/auth/login")) {
      message = "Invalid credentials. Check your email and password.";
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  /** Authenticate with tenant credentials (email + password). */
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  /** Revalidate the current bearer token and fetch the user's identity. */
  me: () => request<MeResponse>("/auth/me"),

  /** Invalidate the current token server-side (blacklists its jti). */
  logout: () => request<void>("/auth/logout", { method: "POST" }),

  /** List downloads available for this deployment (requires auth). */
  getDownloads: () => request<DownloadItem[]>("/downloads"),

  /** Public deployment-mode info (no auth). */
  getAvailability: () => request<DownloadAvailability>("/downloads/availability"),
};

/**
 * Origin-relative URL for fetching an installer file. The backend's own
 * downloadUrl embeds its host; we build a proxy-relative path instead so the
 * link works behind the Vite dev proxy and nginx. The /downloads/{file} route
 * is anonymous, so a plain anchor download works.
 */
export function fileDownloadUrl(filename: string): string {
  return `${API_BASE}/downloads/${encodeURIComponent(filename)}`;
}

export { request };
